/**
 * Transport — route + stop master, plus per-student boarding assignment.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

function RouteCard({ route, onAddStop, onAssign }) {
  const [stop, setStop] = useState({ name: "", seq: 1, pickup_time: "", drop_time: "" });
  return (
    <div className="vs-card p-4 space-y-3" data-testid={`route-card-${route.id}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium">{route.name}</div>
          <div className="text-xs text-[var(--vs-text-secondary)] font-mono">{route.code || "—"} · {route.vehicle_no || "—"}</div>
          <div className="text-xs text-[var(--vs-text-secondary)]">Driver: {route.driver_name || "—"} · {route.driver_phone || "—"}</div>
        </div>
        <Button size="sm" variant="outline" data-testid={`assign-${route.id}`} onClick={() => onAssign(route)}>Assign student</Button>
      </div>
      <div>
        <div className="eyebrow mb-1">Stops</div>
        <div className="space-y-1">
          {(route.stops || []).map((s) => (
            <div key={s.id} className="text-sm flex justify-between" data-testid={`stop-${s.id}`}>
              <span>{s.seq}. {s.name}</span>
              <span className="text-xs text-[var(--vs-text-secondary)] font-mono">{s.pickup_time || "—"} / {s.drop_time || "—"}</span>
            </div>
          ))}
          {!route.stops?.length && <div className="text-xs text-[var(--vs-text-secondary)]">No stops yet</div>}
        </div>
        <div className="grid grid-cols-4 gap-2 mt-2">
          <Input data-testid={`stop-name-${route.id}`} placeholder="Stop name" value={stop.name} onChange={(e) => setStop({ ...stop, name: e.target.value })} className="bg-white" />
          <Input data-testid={`stop-seq-${route.id}`} type="number" min="1" value={stop.seq} onChange={(e) => setStop({ ...stop, seq: Number(e.target.value) })} className="bg-white" />
          <Input data-testid={`stop-pickup-${route.id}`} placeholder="HH:MM" value={stop.pickup_time} onChange={(e) => setStop({ ...stop, pickup_time: e.target.value })} className="bg-white font-mono" />
          <Button size="sm" data-testid={`add-stop-${route.id}`} onClick={() => { onAddStop(route, stop); setStop({ name: "", seq: stop.seq + 1, pickup_time: "", drop_time: "" }); }}>Add</Button>
        </div>
      </div>
    </div>
  );
}

function AssignDialog({ open, onClose, route, students, onSubmit }) {
  const [studentId, setStudentId] = useState("");
  const [stopId, setStopId] = useState("");
  const [fare, setFare] = useState(0);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="assign-dialog">
      <div className="bg-white rounded-lg p-5 w-full max-w-md space-y-3">
        <div className="font-medium">Assign student to {route.name}</div>
        <div>
          <Label>Student</Label>
          <Select value={studentId} onValueChange={setStudentId}>
            <SelectTrigger data-testid="assign-student" className="bg-white"><SelectValue placeholder="Pick a student" /></SelectTrigger>
            <SelectContent>{students.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name} · {s.vs_id}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Stop</Label>
          <Select value={stopId} onValueChange={setStopId}>
            <SelectTrigger data-testid="assign-stop" className="bg-white"><SelectValue placeholder="Pick a stop" /></SelectTrigger>
            <SelectContent>{(route.stops || []).map((s) => (<SelectItem key={s.id} value={s.id}>{s.seq}. {s.name}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Monthly fare (₹)</Label>
          <Input data-testid="assign-fare" type="number" min="0" value={fare} onChange={(e) => setFare(Number(e.target.value))} className="bg-white font-mono" />
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="assign-submit" onClick={() => onSubmit({ student_id: studentId, stop_id: stopId, monthly_fare: fare })} disabled={!studentId || !stopId}>Assign</Button>
        </div>
      </div>
    </div>
  );
}

export default function Transport() {
  const [routes, setRoutes] = useState([]);
  const [students, setStudents] = useState([]);
  const [draft, setDraft] = useState({ name: "", code: "", vehicle_no: "", driver_name: "", driver_phone: "" });
  const [assigning, setAssigning] = useState(null);

  const load = () => {
    api.get("/transport/routes").then((r) => setRoutes(r.data));
    api.get("/students").then((r) => setStudents(r.data || []));
  };
  useEffect(load, []);

  const createRoute = async () => {
    if (!draft.name) { toast.error("Route name is required"); return; }
    await api.post("/transport/routes", draft);
    toast.success("Route created");
    setDraft({ name: "", code: "", vehicle_no: "", driver_name: "", driver_phone: "" });
    load();
  };

  const addStop = async (route, stop) => {
    if (!stop.name) { toast.error("Stop name required"); return; }
    await api.post(`/transport/routes/${route.id}/stops`, stop);
    load();
  };

  const assign = async (data) => {
    await api.post("/transport/assign", { route_id: assigning.id, ...data });
    toast.success("Assigned");
    setAssigning(null);
    load();
  };

  return (
    <DashboardLayout>
      <div className="vs-section">
        <h1 className="text-2xl font-semibold">Transport</h1>
        <p className="text-sm text-[var(--vs-text-secondary)]">Manage bus routes, stops, and per-student boarding assignments.</p>
      </div>

      <div className="vs-card p-4 space-y-3">
        <div className="eyebrow">New route</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <Input data-testid="route-name" placeholder="Route name *" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="bg-white" />
          <Input data-testid="route-code" placeholder="Code" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} className="bg-white font-mono" />
          <Input data-testid="route-vehicle" placeholder="Vehicle no" value={draft.vehicle_no} onChange={(e) => setDraft({ ...draft, vehicle_no: e.target.value })} className="bg-white" />
          <Input data-testid="route-driver" placeholder="Driver name" value={draft.driver_name} onChange={(e) => setDraft({ ...draft, driver_name: e.target.value })} className="bg-white" />
          <Input data-testid="route-driver-phone" placeholder="Driver phone" value={draft.driver_phone} onChange={(e) => setDraft({ ...draft, driver_phone: e.target.value })} className="bg-white font-mono" />
        </div>
        <Button data-testid="route-create" onClick={createRoute}>Create route</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {routes.map((r) => (
          <RouteCard key={r.id} route={r} onAddStop={addStop} onAssign={(rt) => setAssigning(rt)} />
        ))}
      </div>

      <AssignDialog open={!!assigning} onClose={() => setAssigning(null)} route={assigning || {}} students={students} onSubmit={assign} />
    </DashboardLayout>
  );
}
